import React from "react";

/**
 * PageHeaderWithStats Component
 *
 * A reusable header component with a decorative background pattern,
 * title section with optional badge, and a stats grid.
 *
 * @example
 * // Classes Page Example
 * <PageHeaderWithStats
 *   badge={{ text: "Fitness Classes", showPulse: true }}
 *   title={{ main: "Find Your Perfect", highlighted: "Class" }}
 *   description="Explore a wide variety of fitness and wellness classes..."
 *   stats={[
 *     {
 *       icon: CalendarIcon,
 *       value: 150,
 *       label: "Total Classes",
 *     },
 *     {
 *       icon: StarIcon,
 *       value: 4.8,
 *       label: "Avg Rating",
 *       formatter: (val) => val.toFixed(1),
 *     },
 *   ]}
 *   loading={false}
 * />
 *
 * @example
 * // Instructors Page Example
 * <PageHeaderWithStats
 *   badge={{ text: "Expert Instructors" }}
 *   title={{ main: "Meet Our", highlighted: "Instructors" }}
 *   description="Connect with certified wellness professionals..."
 *   stats={[
 *     {
 *       icon: UserGroupIcon,
 *       value: 45,
 *       label: "Instructors",
 *     },
 *     {
 *       icon: StarIcon,
 *       value: 4.9,
 *       label: "Average Rating",
 *       formatter: (val) => `${val}/5`,
 *     },
 *     {
 *       icon: AcademicCapIcon,
 *       value: 250,
 *       label: "Total Classes",
 *     },
 *   ]}
 * />
 */

interface StatItem {
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  value: number | string;
  label: string;
  isVisible?: boolean;
  formatter?: (value: number | string) => string;
}

interface PageHeaderWithStatsProps {
  badge?: {
    text: string;
    showPulse?: boolean;
  };
  title: {
    main: string;
    highlighted: string;
  };
  description: string;
  stats: StatItem[];
  loading?: boolean;
}

const PageHeaderWithStats: React.FC<PageHeaderWithStatsProps> = ({
  badge,
  title,
  description,
  stats,
  loading = false,
}) => {
  // Filter visible stats
  const visibleStats = stats.filter((stat) => stat.isVisible !== false);

  return (
    <div className="zoom-container">
      {/* Header Section */}
      <div className="relative overflow-hidden">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3csvg width='60' height='60' xmlns='http://www.w3.org/2000/svg'%3e%3cdefs%3e%3cpattern id='a' patternUnits='userSpaceOnUse' width='60' height='60'%3e%3ccircle fill='%23f1f5f9' opacity='0.4' cx='30' cy='30' r='1.5'/%3e%3c/pattern%3e%3c/defs%3e%3crect width='100%25' height='100%25' fill='url(%23a)'/%3e%3c/svg%3e")`,
            opacity: 0.3,
          }}
        />
        <div className="relative px-4 sm:px-6 py-12 sm:py-16 md:py-24">
          <div className="max-w-4xl mx-auto text-center">
            {badge && (
              <div className="inline-flex items-center gap-2 bg-white/95 backdrop-blur-sm rounded-full px-4 sm:px-5 py-2 sm:py-3 mb-6 sm:mb-8 border border-gray-200 shadow-lg">
                {badge.showPulse !== false && (
                  <div className="w-2 h-2 bg-orange-500 rounded-full animate-pulse" />
                )}
                <span className="text-gray-700 text-xs sm:text-sm font-medium">
                  {badge.text}
                </span>
              </div>
            )}
            <h1 className="text-xl sm:text-2xl md:text-xl lg:text-2xl font-bold text-gray-900 mb-4 sm:mb-6 leading-tight">
              {title.main}
              <span className="text-orange-500"> {title.highlighted}</span>
            </h1>
            <p className="text-sm sm:text-base md:text-sm lg:text-lg text-gray-600 mb-6 sm:mb-8 max-w-2xl mx-auto font-light leading-relaxed">
              {description}
            </p>
          </div>
        </div>
      </div>

      {/* Stats Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 -mt-6 sm:-mt-8 relative z-10 mb-8 sm:mb-12">
        <div
          className={`grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4 ${
            visibleStats.length === 6
              ? "lg:grid-cols-6"
              : visibleStats.length === 5
                ? "lg:grid-cols-5"
                : visibleStats.length === 4
                  ? "lg:grid-cols-4"
                  : visibleStats.length === 3
                    ? "lg:grid-cols-3"
                    : visibleStats.length === 2
                      ? "lg:grid-cols-2"
                      : "lg:grid-cols-1"
          }`}
        >
          {visibleStats.map((stat, index) => {
            const IconComponent = stat.icon;
            const displayValue = stat.formatter
              ? stat.formatter(stat.value)
              : stat.value;

            return (
              <div
                key={index}
                className="bg-white/95 backdrop-blur-sm p-3 sm:p-4 rounded-xl border border-gray-200 text-center shadow-sm hover:shadow-md transition-shadow"
              >
                <IconComponent className="w-6 h-6 sm:w-8 sm:h-8 text-orange-500 mx-auto mb-2" />
                {loading ? (
                  <div className="animate-pulse">
                    <div className="h-6 sm:h-8 bg-gray-200 rounded w-12 mx-auto mb-2"></div>
                    <div className="h-3 sm:h-4 bg-gray-200 rounded w-16 sm:w-20 mx-auto"></div>
                  </div>
                ) : (
                  <>
                    <p className="text-lg sm:text-2xl font-bold text-gray-900">
                      {displayValue}
                    </p>
                    <p className="text-xs sm:text-sm text-gray-600">
                      {stat.label}
                    </p>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default PageHeaderWithStats;
